import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../../redux/modules/userSlice";
import { UserAPI, MedicalAPI } from "../api";
import CandidateInfo from "./CandidateInfo";
import EmploymentHistory from "./EmploymentHistory";
import EducationHistory from "./EducationHistory";
import BankDetails from "./BankDetails";
import InsuranceDetails from "./InsuranceDetails";
import JoineeFormShell, { JoineeFormFooterActions } from "./JoineeFormShell";
import { JOINEE_FORM_STEPS } from "../../config/joineeFormConstants";
import {
  canShowHrApprove,
  canShowHrResubmit,
  canShowHrReviewActions,
  isJoineeEmployeeFormLocked,
  isJoineeFormDone,
} from "../../config/joineeConstants";
import ApprovedCustomizedDialogs from "./../Model/ModelApproved";
import ReSubmitCustomizedDialogs from "./../Model/ModelReSubmit";
import HrResubmitNotice from "../Joinee/HrResubmitNotice";

var gettingStartedStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 610,
  maxWidth: "92vw",
  bgcolor: "background.paper",
  boxShadow: "0px 0px 29px 8px rgba(0, 0, 0, 0.1)",
  p: 4,
  borderRadius: "12px",
};

function NewJoinForm(props) {
  // HR review page renders its own section tabs and drives `step`.
  var stepControlled = typeof props.step === "number";
  var [activeStep, setActiveStep] = useState(stepControlled ? props.step : 0);
  var [buttonStates, setButtonStates] = useState({
    sectionA: false,
    sectionB: false,
    sectionC: false,
    sectionD: false,
    sectionE: false,
  });
  var [open, setOpen] = useState(false);
  var [busy, setBusy] = useState(false);
  var [approvedComment, setApprovedComment] = useState("");
  var [reSubmitComment, setReSubmitComment] = useState("");
  var [newJoineeFlagButton, setNewJoineeFlagButton] = useState("");

  var navigate = useNavigate();
  var params = useParams();
  var id = params.id ? params.id : "";
  var redux = useSelector(selectUser);
  var user = redux && redux.user ? redux.user : {};
  var isAdmin = Boolean(user.isAdmin);
  var candidateRef = useRef(null);
  var employmentRef = useRef(null);
  var educationRef = useRef(null);
  var bankRef = useRef(null);
  var insuranceRef = useRef(null);

  function changeTabs(tab) {
    setActiveStep(tab);
    if (props.onStepChange) {
      props.onStepChange(tab);
    }
  }

  useEffect(
    function () {
      if (stepControlled) {
        setActiveStep(props.step);
      }
    },
    [stepControlled, props.step]
  );

  function handleButtonClick(key, fresher) {
    if (key === "sectionA") {
      setButtonStates(function (prev) {
        return Object.assign({}, prev, {
          sectionA: true,
          sectionB: fresher ? true : prev.sectionB,
        });
      });
    } else {
      setButtonStates(function (prev) {
        var next = Object.assign({}, prev);
        next[key] = true;
        return next;
      });
    }
  }

  var isLastButtonEnabled = Object.values(buttonStates).every(function (e) {
    return e;
  });

  function handleCloseGettingStarted() {
    if (user && user._id) {
      UserAPI.updateGettingStarted(user._id);
    }
    setOpen(false);
  }

  function handleStatusChange() {
    UserAPI.newJoineeStatus(user._id)
      .then(function (response) {
        if (response && response.success) {
          setNewJoineeFlagButton("submitted");
          navigate("/");
        }
      })
      .catch(function (err) {
        console.log(err);
      });
  }

  useEffect(
    function () {
      var userId = isAdmin ? id : user && user._id;
      if (!userId) {
        return;
      }
      UserAPI.getUser(userId)
        .then(function (res) {
          var data = res && res.data ? res.data : res;
          var status =
            data && data.isNewJoineeFormComplete
              ? data.isNewJoineeFormComplete
              : "";
          setNewJoineeFlagButton(status);
          setReSubmitComment(
            data && data.newJoineeFormComment ? data.newJoineeFormComment : ""
          );
          if (isAdmin) {
            return;
          }
          // After submit/approval, keep all completed sections viewable (not locked).
          if (isJoineeFormDone(status)) {
            setButtonStates({
              sectionA: true,
              sectionB: true,
              sectionC: true,
              sectionD: true,
              sectionE: true,
            });
            return;
          }
          // In-progress / resubmit: unlock steps when later section data already exists.
          if (
            status === "pending" ||
            status === "draft" ||
            status === "resubmission required" ||
            !status
          ) {
            MedicalAPI.getMedicalInsurance(userId)
              .then(function (insRes) {
                var rows =
                  insRes && insRes.data && insRes.data.length
                    ? insRes.data
                    : [];
                if (rows.length) {
                  setButtonStates({
                    sectionA: true,
                    sectionB: true,
                    sectionC: true,
                    sectionD: true,
                    sectionE: true,
                  });
                }
              })
              .catch(function () {});
          }
        })
        .catch(function () {});
    },
    [isAdmin, id, user && user._id]
  );

  useEffect(function () {
    function updateDraftStatus(event) {
      if (event.detail && event.detail.formType === "newJoinee") {
        setNewJoineeFlagButton(event.detail.status);
      }
    }
    window.addEventListener("formStatusChanged", updateDraftStatus);
    return function () {
      window.removeEventListener("formStatusChanged", updateDraftStatus);
    };
  }, []);

  var formApprovedProps = {
    userId: props.id || id,
    formState: "isNewJoineeFormComplete",
    nextURL: "AdminSDF",
    approvedComment: approvedComment,
    setApprovedComment: setApprovedComment,
  };

  var formReSubmitProps = {
    userId: props.id || id,
    formState: "isNewJoineeFormComplete",
    nextURL: "AdminSDF",
    reSubmitComment: reSubmitComment,
    setReSubmitComment: setReSubmitComment,
  };

  var stepMeta = JOINEE_FORM_STEPS[activeStep] || JOINEE_FORM_STEPS[0];
  var employeeLocked =
    !isAdmin && isJoineeEmployeeFormLocked(newJoineeFlagButton);
  var formReadOnly = Boolean(isAdmin || employeeLocked);
  var showShellFooter =
    !isAdmin &&
    !employeeLocked &&
    (activeStep === 0 ||
      activeStep === 1 ||
      activeStep === 2 ||
      activeStep === 3 ||
      activeStep === 4);

  function handleBackHome() {
    navigate("/");
  }

  function activeRef() {
    if (activeStep === 0) {
      return candidateRef.current;
    }
    if (activeStep === 1) {
      return employmentRef.current;
    }
    if (activeStep === 2) {
      return educationRef.current;
    }
    if (activeStep === 3) {
      return bankRef.current;
    }
    if (activeStep === 4) {
      return insuranceRef.current;
    }
    return null;
  }

  function handleDraft() {
    var ref = activeRef();
    if (ref && ref.submitDraft) {
      return ref.submitDraft();
    }
    return null;
  }

  function handleNext() {
    var ref = activeRef();
    if (ref && ref.submitNext) {
      ref.submitNext();
    }
  }

  var completedSteps = [
    buttonStates.sectionA,
    buttonStates.sectionB,
    buttonStates.sectionC,
    buttonStates.sectionD,
    buttonStates.sectionE,
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={gettingStartedStyle}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Getting Started
          </Typography>
          <Typography id="modal-modal-description" sx={{ mt: 2 }}>
            Before you proceed with the form filing process, let us give you a
            brief overview. Complete each section of the New Joinee Form, save
            drafts anytime, and submit when ready.
          </Typography>
          <Box sx={{ mt: 2, textAlign: "end" }}>
            <button
              onClick={handleCloseGettingStarted}
              type="button"
              className="btn btn-primary btn-lg"
            >
              Get Started
            </button>
          </Box>
        </Box>
      </Modal>

      {isAdmin && !props.hideHrActions && canShowHrReviewActions(newJoineeFlagButton) ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            mb: 1.5,
            width: { xs: "100%", md: "82%" },
            maxWidth: 780,
            mx: "auto",
          }}
        >
          {canShowHrApprove(newJoineeFlagButton) ? (
            <ApprovedCustomizedDialogs formApprovedProps={formApprovedProps} />
          ) : null}
          {canShowHrResubmit(newJoineeFlagButton) ? (
            <ReSubmitCustomizedDialogs formReSubmitProps={formReSubmitProps} />
          ) : null}
        </Box>
      ) : null}

      {/* Finish button removed — Insurance "Save and finish" marks New Joinee submitted.
      {!isAdmin &&
      isLastButtonEnabled &&
      newJoineeFlagButton !== "review done" ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mb: 1.5,
            width: { xs: "100%", md: "82%" },
            maxWidth: 780,
            mx: "auto",
          }}
        >
          <button
            onClick={handleStatusChange}
            type="button"
            className="btn btn-lg btn-primary"
          >
            Finish New Joinee Form
          </button>
        </Box>
      ) : null}
      */}

      <JoineeFormShell
        activeStep={activeStep}
        onStepChange={changeTabs}
        onBack={!isAdmin ? handleBackHome : null}
        sideNavTitle={stepControlled ? undefined : "New Joinee Form"}
        title={activeStep === 0 ? (stepControlled ? "New Joinee Form" : stepMeta.label) : stepMeta.title}
        subtitle={stepMeta.subtitle}
        completedSteps={completedSteps}
        freeStepNavigation
        hideSteps={stepControlled}
        banner={
          !isAdmin && newJoineeFlagButton === "resubmission required" ? (
            <HrResubmitNotice comment={reSubmitComment} embedded />
          ) : null
        }
        footer={
          showShellFooter ? (
            <JoineeFormFooterActions
              showPrevious={activeStep > 0}
              previousMessage="Switching tabs will discard them."
              onPrevious={function () {
                changeTabs(activeStep - 1);
              }}
              onDraft={handleDraft}
              onNext={handleNext}
              disabled={Boolean(busy)}
              drafting={busy === "draft"}
              saving={busy === "next"}
              nextLabel={activeStep === 4 ? "Save and finish" : "Save and next"}
            />
          ) : null
        }
      >
        {activeStep === 0 ? (
          <CandidateInfo
            formikRef={candidateRef}
            hideInternalActions
            changeTab={changeTabs}
            handleButtonClick={handleButtonClick}
            setApprovedComment={setApprovedComment}
            setReSubmitComment={setReSubmitComment}
            setNewJoineeFlagButton={setNewJoineeFlagButton}
            onBusyChange={setBusy}
            readOnly={formReadOnly}
            id={id}
          />
        ) : null}

        {activeStep === 1 ? (
          <EmploymentHistory
            formikRef={employmentRef}
            hideInternalActions
            changeTab={changeTabs}
            handleButtonClick={handleButtonClick}
            onBusyChange={setBusy}
            readOnly={formReadOnly}
            id={id}
          />
        ) : null}

        {activeStep === 2 ? (
          <EducationHistory
            formikRef={educationRef}
            hideInternalActions
            changeTab={changeTabs}
            handleButtonClick={handleButtonClick}
            onBusyChange={setBusy}
            readOnly={formReadOnly}
            id={id}
          />
        ) : null}

        {activeStep === 3 ? (
          <BankDetails
            formikRef={bankRef}
            hideInternalActions
            changeTab={changeTabs}
            handleButtonClick={handleButtonClick}
            onBusyChange={setBusy}
            readOnly={formReadOnly}
            id={id}
          />
        ) : null}

        {activeStep === 4 ? (
          <InsuranceDetails
            formikRef={insuranceRef}
            hideInternalActions
            changeTab={changeTabs}
            handleButtonClick={handleButtonClick}
            onBusyChange={setBusy}
            readOnly={formReadOnly}
            id={id}
          />
        ) : null}
      </JoineeFormShell>
    </Box>
  );
}

export default NewJoinForm;
